import React, { useState, useEffect, useRef } from "react";
import { X, Send, Loader2, MessageCircle, Volume2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

// Live in-character conversation with an NPC (Claude via the Emergent LLM key). Per-session memory;
// NVIDIA ACE voice/face plugs in behind creds. Fixed full-screen portal so it escapes card clipping.
export default function NpcChatModal({ projectId, node, onClose }) {
  const [npc, setNpc] = useState(null);
  const [turns, setTurns] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [ace, setAce] = useState("NOT_CONNECTED");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);

  const speak = async (text, idx) => {
    if (playing !== null) {
      if (audioRef.current) { audioRef.current.pause(); }
      if (playing === idx) { setPlaying(null); return; }
    }
    setPlaying(idx);
    try {
      const { data } = await api.post(`/gv2/projects/${projectId}/npc/${node.id}/say`, { text: text.slice(0, 800) }, { timeout: 60000 });
      const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      audioRef.current = audio;
      audio.onended = () => setPlaying(null);
      audio.onerror = () => setPlaying(null);
      await audio.play();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't play the voice line");
      setPlaying(null);
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    api.get(`/gv2/projects/${projectId}/npc/${node.id}/chat`)
      .then(({ data }) => { setNpc(data.npc); setTurns(data.turns || []); setSessionId(data.session_id); setAce(data.ace); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't open the conversation"))
      .finally(() => setLoading(false));
  }, [projectId, node.id]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [turns, loading, sending]);

  const send = async () => {
    const text = msg.trim();
    if (!text || sending) return;
    setMsg("");
    setTurns((t) => [...t, { role: "user", text, at: Date.now() }]);
    setSending(true);
    try {
      const { data } = await api.post(`/gv2/projects/${projectId}/npc/${node.id}/chat`, { message: text, session_id: sessionId }, { timeout: 90000 });
      setTurns((t) => [...t, { role: "npc", text: data.reply, at: Date.now() }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The NPC didn't respond — try again.");
      setTurns((t) => t.slice(0, -1));
      setMsg(text);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      data-testid="gv2-npc-chat-modal" onClick={onClose}>
      <div className="relative w-full max-w-lg h-[80vh] max-h-[640px] rounded-2xl overflow-hidden border border-cyan-500/30 bg-[hsl(var(--card))] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[hsl(var(--border))] bg-gradient-to-r from-cyan-600/20 to-violet-600/10">
          <MessageCircle className="w-4 h-4 text-cyan-400" />
          <span className="font-bold text-sm flex-1 truncate">{node.title}</span>
          {npc?.voice && <Badge variant="outline" className="text-[10px] gap-1 border-cyan-500/40 text-cyan-400"><Volume2 className="w-2.5 h-2.5" /> {ace === "CONNECTED" ? "ACE voice" : npc.voice}</Badge>}
          <button onClick={onClose} data-testid="gv2-npc-chat-close" className="p-1 rounded-lg hover:bg-white/10 transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="gv2-npc-chat-log">
          {loading ? (
            <div className="flex items-center justify-center h-full text-[hsl(var(--muted-foreground))]"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <>
              {npc?.greeting && (
                <div className="flex flex-col items-start gap-1">
                  <div className="rounded-2xl rounded-tl-sm bg-[hsl(var(--muted))]/60 px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap" data-testid="gv2-npc-greeting">{npc.greeting}</div>
                  <button onClick={() => speak(npc.greeting, "greeting")} data-testid="gv2-npc-speak-greeting"
                    className="text-[10px] text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1 pl-1">
                    {playing === "greeting" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />} speak
                  </button>
                </div>
              )}
              {turns.map((t, i) => (
                <div key={i} className={`flex flex-col gap-1 ${t.role === "user" ? "items-end" : "items-start"}`}>
                  <div className={`rounded-2xl px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap ${t.role === "user" ? "rounded-tr-sm bg-gradient-to-r from-cyan-600 to-violet-600 text-white" : "rounded-tl-sm bg-[hsl(var(--muted))]/60"}`}
                    data-testid={`gv2-npc-turn-${t.role}-${i}`}>{t.text}</div>
                  {t.role === "npc" && (
                    <button onClick={() => speak(t.text, i)} data-testid={`gv2-npc-speak-${i}`}
                      className="text-[10px] text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1 pl-1">
                      {playing === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />} speak
                    </button>
                  )}
                </div>
              ))}
              {sending && <div className="flex items-start"><div className="rounded-2xl rounded-tl-sm bg-[hsl(var(--muted))]/60 px-3 py-2"><Loader2 className="w-4 h-4 animate-spin" /></div></div>}
            </>
          )}
        </div>

        <div className="p-3 border-t border-[hsl(var(--border))] flex gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder={`Say something to ${node.title}…`} disabled={loading || sending} data-testid="gv2-npc-chat-input"
            className="flex-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40" />
          <Button size="sm" disabled={loading || sending || !msg.trim()} onClick={send} data-testid="gv2-npc-chat-send"
            className="rounded-full h-9 w-9 p-0 shrink-0 bg-gradient-to-r from-cyan-600 to-violet-600">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
